import React, {useContext, useEffect, useState} from 'react';
import {QRCode, Spin, Card, Space, Input, Button, Form, message} from 'antd';
import {useNavigate} from 'react-router-dom';
import {ROUTES} from '../../utils/ROUTES';
import {fetchTwoFactorStatusApi, setupTwoFactorApi} from '../../api/two-factor-api';
import {UserContext} from '../../utils/UserContext';
import {refreshUserDataComplete} from '../../utils/authHelpers';
import {CopyOutlined, InfoCircleOutlined} from '@ant-design/icons';
import {doesNotHaveTwoFactorEnabledAndIsEmailAuth, hasTwoFactorEnabled, hasTwoFactorEnabledAndIsEmailAuth, isNotEmailAuth} from '../../utils/variables/userVariables';

export default function SetupTwoFactor() {
	const navigate = useNavigate();
	const [isLoading, setIsLoading] = useState(true);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isSuccess, setIsSuccess] = useState(false);
	const [response, setResponse] = useState(null);
	const [qrCodeData, setQrCodeData] = useState(null);
	const [form] = Form.useForm();
	const [currentUser, setCurrentUser] = useContext(UserContext);

	const fetchTwoFactorStatus = async () => {
		setIsLoading(true);
		try {
			const response = await fetchTwoFactorStatusApi();

			setResponse(response.data);
			if (response.data.provisioning_url) {
				setQrCodeData(response.data.provisioning_url);
			}
			setIsSuccess(true);
		} catch (error) {
			console.error('Error fetching two factor status:', error);
			message.error('error: ' + error.response?.data?.error);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		fetchTwoFactorStatus();
	}, []);

	const handleSubmit = async (values) => {
		setIsSubmitting(true);

		try {
			await setupTwoFactorApi(values.code);
			await refreshUserDataComplete(setCurrentUser);
			message.success('Two-factor authentication has been successfully set up!');
			navigate(ROUTES.DASHBOARD);
		} catch (error) {
			console.error('Error setting up two factor authentication:', error);
			message.error('error: ' + error.response?.data?.error);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleCopySecret = () => {
		if (response?.secret) {
			navigator.clipboard
				.writeText(response.secret)
				.then(() => {
					message.success('Secret key copied to clipboard');
				})
				.catch((error) => {
					console.error('Failed to copy secret key:', error);
					message.error('Failed to copy secret key');
				});
		}
	};

	return (
		<div className="max-w-3xl">
			{isLoading && (
				<div className="flex justify-center items-center h-64">
					<Spin size="large" />
				</div>
			)}
			{!isLoading && hasTwoFactorEnabledAndIsEmailAuth(currentUser) && (
				<Card>
					<Space direction="vertical" size="large">
						<div>
							<h2 className="text-xl font-semibold mb-2">Two-Factor Authentication Enabled</h2>
							<p className="text-gray-600">Your account is currently protected with two-factor authentication.</p>
						</div>

						<div className="bg-green-50 border border-green-200 rounded-md p-4">
							<div className="flex items-center">
								<div className="flex-shrink-0">
									<svg className="h-5 w-5 text-green-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
										<path
											fillRule="evenodd"
											d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
											clipRule="evenodd"
										/>
									</svg>
								</div>
								<div className="ml-3">
									<p className="text-sm font-medium text-green-800">Your account has an extra layer of security</p>
								</div>
							</div>
						</div>

						<div className="space-y-4">
							<div className="border border-gray-200 rounded-md p-4">
								<h3 className="text-md font-medium mb-2">What this means:</h3>
								<ul className="list-disc ml-5 space-y-1 text-gray-700">
									<li>You'll need to enter a verification code from your authenticator app when signing in</li>
									<li>This helps protect your account even if your password is compromised</li>
									<li>Make sure you keep your authenticator app accessible</li>
								</ul>
							</div>

							<div className="border border-gray-200 rounded-md p-4">
								<h3 className="text-md font-medium mb-2">Recovery options:</h3>
								<p className="text-gray-700 mb-2">If you lose access to your authenticator app, you'll need to contact support to regain access to your account.</p>
							</div>
						</div>

						<div className="mt-4">
							<p className="text-grey mb-2 text-sm">Need support?</p>
							<Button type="primary" onClick={() => navigate(ROUTES.CONTACT_US)}>
								Contact Support
							</Button>
						</div>
					</Space>
				</Card>
			)}
			{!isLoading && doesNotHaveTwoFactorEnabledAndIsEmailAuth(currentUser) && isSuccess && qrCodeData && (
				<Card>
					<Space direction="vertical" size="large">
						<div>
							<h2 className="text-xl font-semibold mb-2">Enable 2FA</h2>
						</div>

						<ol className="list-decimal ml-5 space-y-6">
							<li className="">
								<div className="flex flex-col md:flex-row gap-6 items-start">
									<div>
										You will need an authenticator mobile app to complete this process, such as one of the following:
										<div className="mt-2 space-y-1">
											<div className="text-green-600">Google Authenticator</div>
											<div className="text-green-600">Microsoft Authenticator</div>
											<div className="text-green-600">Authy</div>
										</div>
									</div>
									<div>
										<QRCode value={qrCodeData} size={180} bordered />
									</div>
								</div>
							</li>
							<li className="text-gray-800">
								Scan the QR code with your authenticator
								<div className="text-sm">
									If you can't scan the code, you can enter this secret key into your authentication app
									<div className="mt-2 border border-gray-300 rounded p-2 flex justify-between items-center">
										<span className="font-mono">{response?.secret}</span>
										<Button type="text" className="bg-backgroundGrey hover:bg-backgroundGrey/30" icon={<CopyOutlined />} onClick={handleCopySecret} />
									</div>
								</div>
							</li>
							<li className="text-gray-800">
								After scanning the QR code above, enter the six-digit code generated by your authenticator
								<Form form={form} onFinish={handleSubmit} layout="vertical" style={{marginTop: 16}}>
									<Form.Item name="code" rules={[{required: true, message: 'Please input your verification code!'}]} className="w-full">
										<Input.OTP
											size="large"
											length={6}
											autoFocus
											inputType="numeric"
											className="w-full"
											style={{boxSizing: 'border-box'}}
											inputStyle={{width: '100%', aspectRatio: '1/1'}}
										/>
									</Form.Item>
									<div className="flex mt-4">
										<Button type="primary" htmlType="submit" loading={isSubmitting}>
											Verify now
										</Button>
									</div>
								</Form>
							</li>
						</ol>
					</Space>
				</Card>
			)}
			{!isLoading && isNotEmailAuth(currentUser) && (
				<Card>
					<Space direction="vertical" size="large">
						<div>
							<h2 className="text-xl font-semibold mb-2">Two-Factor Authentication</h2>
							<p className="text-charcoal mb-4">Your account is secured through Google's built-in authentication.</p>
							<div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4">
								<div className="flex items-start">
									<InfoCircleOutlined className="text-blue-500 mt-1 mr-2" />
									<div>
										<p className="font-medium">Google Authentication is already enabled</p>
										<p className="text-sm">
											Your account uses Google's built-in security features, which include two-factor authentication. You can manage these security settings through your Google
											account.
										</p>
									</div>
								</div>
							</div>
						</div>
					</Space>
				</Card>
			)}
		</div>
	);
}
